import { useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Trash2, Download, FileSpreadsheet } from "lucide-react";

const CONFIRM_PHRASE = "USUN-DANE";

const OPTIONS = [
  {
    key: "orders",
    label: "Zamówienia (wszystkie statusy)",
    desc: "Usuwa wszystkie zamówienia i powiązane logi zmian. Resetuje także wykorzystany budżet (liczony z zamówień).",
  },
  {
    key: "custom_product_requests",
    label: "Zapytania o produkty spoza katalogu",
    desc: "Usuwa wszystkie zapytania, niezależnie od statusu.",
  },
  {
    key: "budget_increase_requests",
    label: "Prośby o zwiększenie budżetu",
    desc: "Usuwa wszystkie wnioski budżetowe.",
  },
  {
    key: "reset_order_counter",
    label: "Reset numeracji zamówień",
    desc: "Wyzeruje sekwencję `OL/YYYY/NNNNN` — następne zamówienie zacznie się od 00001.",
  },
];

export default function AdminCleanup() {
  const [sel, setSel] = useState({});
  const [confirm, setConfirm] = useState("");
  const [working, setWorking] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (k) => setSel({ ...sel, [k]: !sel[k] });
  const selectedKeys = Object.keys(sel).filter((k) => sel[k]);

  const exportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export-all", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `export_office_land_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Eksport pobrany");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nie udało się wyeksportować"));
    } finally {
      setExporting(false);
    }
  };

  const submit = async () => {
    if (selectedKeys.length === 0) {
      toast.error("Wybierz przynajmniej jedną kategorię do usunięcia");
      return;
    }
    if (confirm !== CONFIRM_PHRASE) {
      toast.error(`Wpisz dokładnie: ${CONFIRM_PHRASE}`);
      return;
    }
    if (!window.confirm("OSTATNIE OSTRZEŻENIE: Tej operacji nie da się cofnąć. Czy na pewno kontynuować?")) return;
    setWorking(true);
    setResult(null);
    try {
      const payload = { confirm };
      OPTIONS.forEach((o) => { payload[o.key] = !!sel[o.key]; });
      const res = await api.post("/admin/wipe", payload);
      setResult(res.data);
      toast.success("Dane usunięte");
      setSel({});
      setConfirm("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nie udało się usunąć danych"));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <PageHeader
        testId="admin-cleanup"
        title="Czyszczenie danych"
        subtitle="Przygotuj portal dla nowego klienta — usuń dane transakcyjne pozostawiając konfigurację"
      />

      <div className="ol-card p-5 mb-6 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="export-all-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-md bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <FileSpreadsheet className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 mb-1">Eksportuj wszystko (backup XLSX)</p>
            <p className="text-sm text-slate-600 mb-3 leading-relaxed">
              Jeden plik XLSX z osobnymi zakładkami dla budów, użytkowników, produktów, zamówień (+ pozycji i logu zmian),
              zapytań spoza katalogu i wniosków budżetowych. <strong>Zalecane przed każdym czyszczeniem danych.</strong>
            </p>
            <button
              onClick={exportAll}
              disabled={exporting}
              data-testid="export-all-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            >
              <Download className="w-4 h-4" />
              {exporting ? "Generuję eksport…" : "Pobierz pełny backup"}
            </button>
          </div>
        </div>
      </div>

      <div className="ol-card p-5 mb-6 border-2 border-red-200 bg-red-50/40">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-red-900 mb-1">Operacja nieodwracalna</p>
            <p className="text-sm text-red-800 leading-relaxed">
              Usunięcie danych jest <strong>natychmiastowe i trwałe</strong>. Nie zostaną one zachowane w żadnym koszu ani backupie aplikacji.
              Operacja <strong>nie usuwa</strong> użytkowników, budów, produktów ani reguł akceptacji — tylko dane transakcyjne.
            </p>
          </div>
        </div>
      </div>

      <div className="ol-card p-5 mb-4">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Co usunąć</p>
        <div className="space-y-2">
          {OPTIONS.map((o) => (
            <label
              key={o.key}
              className={`block p-3 rounded-md border cursor-pointer transition-colors ${
                sel[o.key] ? "border-[#FF6B1A] bg-[#FFF4ED]" : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
              data-testid={`cleanup-${o.key}`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={!!sel[o.key]}
                  onChange={() => toggle(o.key)}
                  className="mt-1 accent-[#FF6B1A] w-4 h-4"
                  data-testid={`cleanup-checkbox-${o.key}`}
                />
                <div className="flex-1">
                  <p className="font-semibold text-slate-900 text-sm">{o.label}</p>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{o.desc}</p>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="ol-card p-5 mb-4">
        <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Potwierdzenie
        </label>
        <p className="text-xs text-slate-600 mb-2">
          Aby kontynuować wpisz dokładnie: <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-900 font-mono text-xs">{CONFIRM_PHRASE}</code>
        </p>
        <input
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={CONFIRM_PHRASE}
          data-testid="cleanup-confirm"
          className="w-full sm:w-80 px-3 py-2 rounded-md border-2 border-slate-300 text-sm font-mono focus:border-red-500 focus:outline-none"
        />
      </div>

      <button
        onClick={submit}
        disabled={working || selectedKeys.length === 0 || confirm !== CONFIRM_PHRASE}
        data-testid="cleanup-submit"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-red-600 hover:bg-red-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
      >
        <Trash2 className="w-4 h-4" />
        {working ? "Usuwanie…" : `Usuń wybrane dane (${selectedKeys.length})`}
      </button>

      {result && (
        <div className="ol-card p-5 mt-6 border-emerald-200 bg-emerald-50/40" data-testid="cleanup-result">
          <p className="font-bold text-emerald-900 mb-2">Operacja zakończona</p>
          <ul className="text-sm text-emerald-800 space-y-1">
            {Object.entries(result.deleted || {}).map(([k, v]) => (
              <li key={k}><strong>{k}:</strong> usunięto {v}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
